import React, { useState, useEffect } from 'react';
import TopBar from './TopBar';
import { ELEMENTS, EMPTY_ELEMENT_ENTRY } from '../utils/constants';
import { getCycleSummary } from '../utils/cycle';

function PreviousDay({ state, setState, setRoute }) {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Get yesterday's date
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  // Load yesterday's entry data
  useEffect(() => {
    const entry = state.entries.find(e => e.date === yesterdayStr);
    setSelectedEntry(entry);

    if (entry) {
      // Normalize entry values to include cycleNote
      const normalizedValues = {};
      ELEMENTS.forEach((el) => {
        normalizedValues[el.key] = { ...EMPTY_ELEMENT_ENTRY, ...(entry.values[el.key] || {}) };
      });
      setTempValues(normalizedValues);
    } else {
      // Create blank entry for yesterday
      const blankValues = {};
      ELEMENTS.forEach((el) => {
        blankValues[el.key] = { ...EMPTY_ELEMENT_ENTRY };
      });
      setTempValues(blankValues);
    }
  }, [state.entries, yesterdayStr]);

  // Check for changes
  useEffect(() => {
    if (selectedEntry) {
      const hasUnsavedChanges = Object.keys(tempValues).some(key => {
        const original = { ...EMPTY_ELEMENT_ENTRY, ...(selectedEntry.values[key] || {}) };
        const current = { ...EMPTY_ELEMENT_ENTRY, ...(tempValues[key] || {}) };
        return original.text !== current.text ||
          original.action !== current.action ||
          original.redirect !== current.redirect ||
          (original.cycleNote || "") !== (current.cycleNote || "") ||
          original.rating !== current.rating;
      });
      setHasChanges(hasUnsavedChanges);
    } else {
      // For new entries, check if any field has content
      const hasContent = Object.values(tempValues).some(value =>
        value.text.trim() !== "" || value.action.trim() !== "" || (value.cycleNote || "").trim() !== ""
      );
      setHasChanges(hasContent);
    }
  }, [tempValues, selectedEntry]);

  const updateValue = (elementKey, field, value) => {
    setTempValues(prev => ({
      ...prev,
      [elementKey]: {
        ...prev[elementKey],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    const newEntry = { date: yesterdayStr, values: tempValues };
    const existingIndex = state.entries.findIndex(e => e.date === yesterdayStr);

    let newEntries;
    if (existingIndex >= 0) {
      newEntries = [...state.entries];
      newEntries[existingIndex] = newEntry;
    } else {
      newEntries = [...state.entries, newEntry].sort((a, b) => b.date.localeCompare(a.date));
    }

    setState(prev => ({ ...prev, entries: newEntries }));
    setRoute("home");
  };

  const formatYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get cycle summary for yesterday
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const cycleSummary = getCycleSummary(state.cycleTracking, yesterdayDate);
  const showCycleInfo = cycleSummary.enabled && cycleSummary.cycleDay;

  return (
    <div className="p-4">
      <TopBar title={`Edit • ${formatYesterday()}`} onBack={() => setRoute("home")} />

      <div className="mt-3">
        {/* Yesterday Info */}
        <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Editing Yesterday's Entry</strong>
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-300 mt-1">
            {formatYesterday()} • {yesterdayStr}
          </div>
        </div>

        {/* Cycle Snapshot for Yesterday */}
        {showCycleInfo && (
          <div className="mb-4 p-4 rounded-xl bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700">
            <div className="text-xs uppercase tracking-wide text-pink-700 dark:text-pink-200">Cycle snapshot</div>
            <div className="mt-2 text-lg font-semibold text-pink-800 dark:text-pink-100">
              Day {cycleSummary.cycleDay}{cycleSummary.totalLength ? ` of ${cycleSummary.totalLength}` : ''}
            </div>
            {cycleSummary.stage && (
              <div className="mt-1 text-sm text-pink-700 dark:text-pink-100">
                {cycleSummary.stage.name} • Day {cycleSummary.dayOfStage} of {cycleSummary.stage.length}
              </div>
            )}
          </div>
        )}

        {/* Element Editing */}
        <div className="space-y-4">
          {ELEMENTS.map((element) => {
            const elementData = { ...EMPTY_ELEMENT_ENTRY, ...(tempValues[element.key] || {}) };
            return (
              <div key={element.key} className="p-4 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-2xl">{element.emoji}</div>
                  <div>
                    <div className="text-lg font-medium dark:text-dark-text">{element.label}</div>
                    <div className="text-sm text-gray-600 dark:text-dark-textSecondary">{element.hint}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium dark:text-dark-text">Yesterday's state</label>
                    <textarea
                      value={elementData.text || ""}
                      onChange={(e) => updateValue(element.key, "text", e.target.value)}
                      className="w-full mt-1 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text min-h-[80px]"
                      placeholder={`How was your ${element.label.toLowerCase()} yesterday?`}
                    />
                    <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">
                      {(elementData.text || "").length}/300
                    </div>
                  </div>

                  {state.ratingTracking?.enabled && (
                    <div>
                      <label className="text-sm font-medium dark:text-dark-text">How did you feel about {element.label} yesterday?</label>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {state.ratingTracking.emojiSet.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => {
                              const newRating = elementData.rating === emoji ? null : emoji;
                              updateValue(element.key, "rating", newRating);
                            }}
                            className={`text-2xl px-3 py-2 rounded-lg border-2 transition-colors ${
                              elementData.rating === emoji
                                ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg hover:border-gray-400 dark:hover:border-dark-border'
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium dark:text-dark-text">Action for this element</label>
                    <textarea
                      value={elementData.action || ""}
                      onChange={(e) => updateValue(element.key, "action", e.target.value)}
                      className="w-full mt-1 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text min-h-[60px]"
                      placeholder={`What action would have helped your ${element.label.toLowerCase()} yesterday?`}
                    />
                    <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">
                      {(elementData.action || "").length}/300
                    </div>
                  </div>

                  {showCycleInfo && (
                    <div>
                      <label className="text-sm font-medium dark:text-dark-text">Cycle notes (optional)</label>
                      <textarea
                        value={elementData.cycleNote || ""}
                        onChange={(e) => updateValue(element.key, "cycleNote", e.target.value)}
                        className="w-full mt-1 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text min-h-[60px]"
                        placeholder="Anything to notice about your cycle yesterday?"
                      />
                      <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">
                        {(elementData.cycleNote || "").length}/300
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      id={`redirect-${element.key}`}
                      type="checkbox"
                      checked={elementData.redirect || false}
                      onChange={(e) => updateValue(element.key, "redirect", e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor={`redirect-${element.key}`} className="text-sm text-gray-700 dark:text-dark-textSecondary">
                      Was redirectional action required?
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Save/Cancel Buttons */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => setRoute("home")}
            className="flex-1 py-3 rounded-md border dark:border-dark-border dark:bg-dark-surface dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-surfaceHover"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`flex-1 py-3 rounded-md font-medium ${hasChanges
              ? 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
              : 'bg-gray-300 dark:bg-dark-border text-gray-500 dark:text-dark-muted cursor-not-allowed'
              }`}
          >
            {hasChanges ? 'Save Changes' : 'No Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PreviousDay;
